import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from "@/components/ui/table";
import { GraduationCap, Check, Mail, Download, BookOpen } from 'lucide-react';
import { toast } from 'sonner';

const mkTitles = [
  { id: 1, title: "Benny's Big Adventure", language: 'English', publisher: 'Scholastic', age: '5–6 yrs', status: 'provided' },
  { id: 2, title: 'Kisah Si Kancil', language: 'Malay', publisher: 'DBP', age: '5–6 yrs', status: 'provided' },
  { id: 3, title: 'Little Tamil Tales Vol 2', language: 'Tamil', publisher: 'Grassroots', age: '5–6 yrs', status: 'provided' },
  { id: 4, title: 'Xiao Xiong De Meng (小熊的梦)', language: 'Chinese', publisher: 'EPH', age: '5–6 yrs', status: 'provided' },
  { id: 5, title: 'The Magic Seed', language: 'English', publisher: 'MC Edu', age: '5–6 yrs', status: 'provided' },
];

const mkDeliveries = [
  { name: 'MK @ Bendemeer', district: 'Toa Payoh', books: 20, dispatch: '25 Sep 2026', status: 'in_transit', signed: false, ack: false },
  { name: 'MK @ Queenstown', district: 'Queenstown', books: 20, dispatch: '25 Sep 2026', status: 'delivered', signed: true, ack: true },
  { name: 'MK @ Woodlands', district: 'Woodlands', books: 20, dispatch: '25 Sep 2026', status: 'delivered', signed: true, ack: true },
  { name: 'MK @ Jurong', district: 'Jurong West', books: 20, dispatch: '25 Sep 2026', status: 'issue', signed: true, ack: false },
  { name: 'MK @ Tampines', district: 'Tampines', books: 20, dispatch: '25 Sep 2026', status: 'delivered', signed: true, ack: true },
];

export default function MOEMKProgramme() {
  const handleBulkReminder = () => {
    toast.success('Reminders sent to 16 pending MKs');
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-3">
          <GraduationCap className="w-7 h-7 text-[#1A3C6E]" />
          MOE Kindergarten Print Collection Programme
        </h1>
        <p className="text-slate-500 mt-1">2026 | 58 MOE Kindergartens | $550/MK</p>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="overview">Programme Overview</TabsTrigger>
          <TabsTrigger value="titles">Title Provision</TabsTrigger>
          <TabsTrigger value="quotes">Quote Approval</TabsTrigger>
          <TabsTrigger value="delivery">Delivery Monitor</TabsTrigger>
          <TabsTrigger value="invoice">Invoice Approval</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6">
          <Card>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="text-2xl font-bold text-slate-900">58</div>
                  <div className="text-sm text-slate-500">Total MKs</div>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="text-2xl font-bold text-slate-900">$550</div>
                  <div className="text-sm text-slate-500">Budget per MK</div>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="text-2xl font-bold text-slate-900">$31,900</div>
                  <div className="text-sm text-slate-500">Total Programme</div>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="text-2xl font-bold text-slate-900">≥20</div>
                  <div className="text-sm text-slate-500">Books per MK</div>
                </div>
              </div>
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Target age:</strong> 5–6 years | <strong>Languages:</strong> EL, CL, ML, TL | 
                  <strong>Delivery target:</strong> Within 5 months of title provision to Civica
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Title Provision Tab */}
        <TabsContent value="titles" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">MK 2026 Book List — 20 titles</CardTitle>
              <CardDescription>MOE HQ provides the title list for MKs. Civica sources and acquires.</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>#</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Language</TableHead>
                    <TableHead>Publisher</TableHead>
                    <TableHead>Age</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {mkTitles.map((title, idx) => (
                    <TableRow key={title.id} className="table-row-striped">
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell className="font-medium">{title.title}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={
                          title.language === 'English' ? 'bg-blue-50 text-blue-700' :
                          title.language === 'Malay' ? 'bg-green-50 text-green-700' :
                          title.language === 'Tamil' ? 'bg-orange-50 text-orange-700' :
                          'bg-red-50 text-red-700'
                        }>
                          {title.language}
                        </Badge>
                      </TableCell>
                      <TableCell>{title.publisher}</TableCell>
                      <TableCell>{title.age}</TableCell>
                      <TableCell>
                        <Badge className="bg-emerald-50 text-emerald-700">
                          <Check className="w-3 h-3 mr-1" /> Provided
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <p className="text-sm text-slate-500 mt-4">Showing 5 of 20 titles</p>
              <Button className="mt-4 bg-[#1A3C6E] hover:bg-[#142E54]">
                <Check className="w-4 h-4 mr-2" />
                Confirm Title List & Send to Civica
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Quote Approval Tab */}
        <TabsContent value="quotes" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">MK Quote Approval</CardTitle>
              <CardDescription>All 20 quotes received from 3 suppliers each. Civica recommendation submitted.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="p-4 bg-emerald-50 rounded-lg mb-4">
                <p className="text-emerald-800">✓ All 20 recommended. No stock issues.</p>
              </div>
              <div className="flex gap-2">
                <Button className="bg-emerald-600 hover:bg-emerald-700">
                  <Check className="w-4 h-4 mr-2" />
                  Approve All 20 MK Quotes
                </Button>
                <Button variant="outline">
                  Confirm & Issue PO to Civica
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Delivery Monitor Tab */}
        <TabsContent value="delivery" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="font-heading">Delivery Monitor</CardTitle>
                <CardDescription>Acknowledged: 42/58 | Pending: 16 | Discrepancies: 1</CardDescription>
              </div>
              <Button variant="outline" onClick={handleBulkReminder} data-testid="bulk-remind-mks">
                <Mail className="w-4 h-4 mr-2" />
                Send Reminders to 16 Pending MKs
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>MK</TableHead>
                    <TableHead>District</TableHead>
                    <TableHead>Books</TableHead>
                    <TableHead>Dispatch</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Signed DO</TableHead>
                    <TableHead>Acknowledgement</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {mkDeliveries.map((mk, idx) => (
                    <TableRow key={idx} className="table-row-striped">
                      <TableCell className="font-medium">{mk.name}</TableCell>
                      <TableCell>{mk.district}</TableCell>
                      <TableCell>{mk.books}</TableCell>
                      <TableCell>{mk.dispatch}</TableCell>
                      <TableCell>
                        {mk.status === 'delivered' && <Badge className="bg-emerald-50 text-emerald-700">✓ Delivered</Badge>}
                        {mk.status === 'in_transit' && <Badge className="bg-blue-50 text-blue-700">🚚 In Transit</Badge>}
                        {mk.status === 'issue' && <Badge className="bg-amber-50 text-amber-700">⚠️ Discrepancy</Badge>}
                      </TableCell>
                      <TableCell>
                        {mk.signed ? <Check className="w-4 h-4 text-emerald-600" /> : <span className="text-slate-400">Pending</span>}
                      </TableCell>
                      <TableCell>
                        {mk.ack ? (
                          <Badge className="bg-emerald-50 text-emerald-700">✓ Acknowledged</Badge>
                        ) : (
                          <Badge className="bg-amber-50 text-amber-700">⏳ Pending</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <p className="text-sm text-slate-500 mt-4">Showing 5 of 58 MKs</p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invoice Approval Tab */}
        <TabsContent value="invoice" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Invoice Approval</CardTitle>
              <CardDescription>Annual invoice issued after ALL 58 MKs acknowledge receipt.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="p-4 bg-amber-50 rounded-lg mb-4">
                <p className="text-amber-800">
                  <strong>Status:</strong> 42/58 acknowledged. Invoice on hold.
                </p>
              </div>
              <div className="p-4 border rounded-lg">
                <p className="text-sm text-slate-500 mb-2">Preview:</p>
                <p className="font-semibold">INV-MK-2026-001 | Civica → MOE HQ | $31,900 | Itemised by title</p>
              </div>
              <Button className="mt-4" disabled>
                <Check className="w-4 h-4 mr-2" />
                Approve Invoice Once All 58 MKs Acknowledge
              </Button>
              <p className="text-xs text-slate-500 mt-2">Button will be enabled when 58/58 MKs have acknowledged</p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
